import { getUserName } from '../Utils/GeneralUtils';
import { createDefaultAdapter } from './cacheAdapters';

/**
 * The Troubleshooting offline copy: the last good issues answer plus the
 * article and walkthrough reads behind its links, so the pages still open
 * when the gym Wi-Fi drops. Reads only; nothing here is ever sent anywhere.
 *
 * Rules the rest of the app relies on:
 * - Every entry belongs to the signed-in name. `get` never returns another
 *   person's entry and `set` first drops everyone else's, because the shared
 *   iPad changes hands inside one tab and one trainer must not see another's
 *   cached data.
 * - `clear` (sign-in, sign-out, kiosk switch) bumps a generation so a write
 *   already in flight for the previous person is discarded.
 * - A failing store (blocked, full, no IndexedDB) makes every call answer
 *   `null` or `false`; the caller shows the live error as if no cache existed.
 * - The cache is capped in bytes and entries. The oldest entry goes first,
 *   except the two small index entries the pages cannot work without.
 */

export const PINNED = ['issues', 'linked'];
const MAX_ENTRIES = 150;
// One oversized article must not push the whole cache out.
const MAX_ENTRY_SHARE = 0.25;

/** The signed-in name, or null; a corrupt `user` cookie must degrade to "no cache", not throw into a page. */
export const safeUserName = () => {
    try {
        return getUserName() || null;
    } catch (e) {
        return null;
    }
};

const recordId = (user, name) => JSON.stringify([user, name]);

export const createTroubleshootingCache = ({
    adapter,
    getUser = safeUserName,
    now = Date.now,
    maxBytes,
    maxEntries = MAX_ENTRIES,
} = {}) => {
    let generation = 0;
    let tail = Promise.resolve();

    // One queue, so a read issued after a write or a clear always sees it.
    const enqueue = (task, fallback) => {
        const result = tail.then(task).catch(() => fallback);
        tail = result;
        return result;
    };

    const capBytes = async () => (typeof maxBytes === 'number' ? maxBytes : adapter.maxBytes());

    const evict = async (user) => {
        const all = await adapter.listMeta();
        const mine = [];
        // Leftovers of a previous person are removed whether or not they fit.
        for (const record of all) {
            if (record.user !== user) await adapter.remove(record.id);
            else mine.push(record);
        }
        const cap = await capBytes();
        let total = mine.reduce((sum, r) => sum + r.size, 0);
        let count = mine.length;
        const oldestFirst = mine.filter((r) => !PINNED.includes(r.name)).sort((a, b) => a.cachedAt - b.cachedAt);
        for (const record of oldestFirst) {
            if (total <= cap && count <= maxEntries) break;
            await adapter.remove(record.id);
            total -= record.size;
            count -= 1;
        }
    };

    return {
        generation: () => generation,

        /** `{ value, cachedAt }` for this person's entry, or `null`. */
        get: (name) => {
            const user = getUser();
            if (!user) return Promise.resolve(null);
            return enqueue(async () => {
                const record = await adapter.get(recordId(user, name));
                if (!record || record.user !== user) return null;
                return { value: JSON.parse(record.json), cachedAt: record.cachedAt };
            }, null);
        },

        /**
         * Stores `value` for `forUser`, who must still be the signed-in person
         * when the write runs. Resolves false when nothing was stored.
         */
        set: (name, value, forUser) => {
            if (!forUser || getUser() !== forUser) return Promise.resolve(false);
            const startedIn = generation;
            return enqueue(async () => {
                if (generation !== startedIn || getUser() !== forUser) return false;
                const json = JSON.stringify(value);
                const cap = await capBytes();
                if (json.length > cap * MAX_ENTRY_SHARE) return false;
                await adapter.put({ id: recordId(forUser, name), user: forUser, name, cachedAt: now(), size: json.length, json });
                await evict(forUser);
                return true;
            }, false);
        },

        /** Drops this person's entries (never the pinned ones) whose name `keep` rejects. */
        prune: (keep) => {
            const user = getUser();
            if (!user) return Promise.resolve(false);
            return enqueue(async () => {
                const all = await adapter.listMeta();
                for (const record of all) {
                    if (record.user === user && !PINNED.includes(record.name) && !keep(record.name)) await adapter.remove(record.id);
                }
                return true;
            }, false);
        },

        /** Forgets everything, for every person. */
        clear: () => {
            generation += 1;
            return enqueue(() => adapter.clear().then(() => true), false);
        },
    };
};

export const troubleshootingCache = createTroubleshootingCache({ adapter: createDefaultAdapter() });

/** Sign-in, sign-out and kiosk switch call this through `resetUserCaches`. */
export const clearTroubleshootingCache = () => troubleshootingCache.clear();
